import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CabiBubble from "../../components/Kids/CabiBubble";
import Pill from "../../components/Kids/Pill";
import Button from "../../components/Kids/Button";
import { planFeatureList } from "../../utils/kidsPlanFeatures";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";

const API = process.env.REACT_APP_API_URL;

export default function KidsPlans() {
  const { t }     = useTranslation();
  const navigate = useNavigate();
  const token    = localStorage.getItem("kids_parent_token");

  const [plans, setPlans]     = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null); // slug being upgraded

  useEffect(() => {
    axios.get(`${API}/api/kids/plans`)
      .then((res) => setPlans(res.data?.data || []))
      .catch(() => toast.error("Could not load plans."))
      .finally(() => setLoading(false));
  }, []);

  const choosePlan = async (slug) => {
    if (!token) { navigate("/kids/login"); return; }
    setBusy(slug);
    try {
      const res = await axios.post(
        `${API}/api/kids/subscription/upgrade`,
        { plan_slug: slug, billing_cycle: billing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // No payment gateway yet — this creates a request the admin approves.
      toast.success(res.data?.message || "Request sent to the Cabil team! 🎉");
      navigate("/kids/parent/dashboard");
    } catch (err) {
      if (err.response?.status === 401) { navigate("/kids/login"); return; }
      toast.error(err.response?.data?.message || "Could not send your request.");
    } finally {
      setBusy(null);
    }
  };

  const priceOf = (p) => billing === "yearly" ? p.price_yearly : p.price_monthly;
  const features = planFeatureList;

  return (
    <div
      className="min-h-screen font-body p-4 sm:p-8"
      style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}
    >
      <Seo
        {...SEO["/kids/plans"]}
        path="/kids/plans"
      />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <Link to="/kids/parent/dashboard" className="text-muted hover:text-brand text-sm font-body font-semibold">← Dashboard</Link>
          <Link to="/kids" className="font-display font-semibold text-xl">
            <span className="text-brand">Cabil</span> <span className="text-gold">Kids</span>
          </Link>
        </div>

        <CabiBubble
          mood="celebrating"
          theme="light"
          size={72}
          side="start"
          className="mb-8 mt-4"
          text={
            <>
              <div className="font-display font-semibold text-xl text-ink">{t("kids.plans.title")} 🚀</div>
              <div className="text-muted text-sm font-body font-semibold">Unlock more games, more children, and more learning fun.</div>
            </>
          }
        />

        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white border border-cardline rounded-full p-1 shadow-card gap-1">
            {["monthly", "yearly"].map((b) => (
              <Pill key={b} active={billing === b} onClick={() => setBilling(b)}>
                {b === "monthly" ? t("kids.plans.monthly") : `${t("kids.plans.yearly")} 🎁`}
              </Pill>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-faint font-body font-semibold">Loading plans…</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p, i) => {
              const popular = i === 1;
              return (
                <div key={p.id || p.slug}
                  className={`relative bg-white rounded-card-lg p-6 flex flex-col transition hover:-translate-y-1 ${
                    popular ? "border-2 border-brand shadow-[0_20px_44px_-20px_rgba(238,95,43,.4)]" : "border border-cardline shadow-card"
                  }`}>
                  {popular && (
                    <span className="absolute -top-3 start-1/2 -translate-x-1/2 bg-brand text-white text-[11px] font-body font-extrabold px-3.5 py-1.5 rounded-full">
                      MOST POPULAR
                    </span>
                  )}
                  <h3 className="font-display font-semibold text-xl text-ink">{p.name}</h3>
                  <div className="mt-2.5 mb-0.5">
                    <span className="text-[38px] font-body font-black text-brand">PKR {Number(priceOf(p) || 0).toLocaleString()}</span>
                    <span className="text-faint text-sm">{billing === "yearly" ? t("kids.plans.perYear") : t("kids.plans.perMonth")}</span>
                  </div>
                  <p className="text-muted text-sm mb-4">Up to {p.max_children} child{p.max_children > 1 ? "ren" : ""}</p>

                  <ul className="space-y-2 mb-5 flex-1">
                    {features(p).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-[#5C4A3E] font-body font-semibold">
                        <span className="text-brand font-extrabold mt-0.5">✓</span> {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={popular ? "primary" : "secondary"}
                    onClick={() => choosePlan(p.slug)}
                    disabled={busy === p.slug}
                    className="w-full"
                  >
                    {busy === p.slug ? "Sending…" : t("kids.plans.choosePlan")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
